import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Target, Boxes } from "lucide-react";
import { toast } from "sonner";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
import ComplianceProofButton from "../../design-system/compliance/ComplianceProofButton";
// Release C-2 (P1) — Open in Microsoft 365 Mail for this record surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

const STAGES = ["qualified","site_visit","estimating","proposal","negotiation","won","lost"];

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [attachId, setAttachId] = useState(null);
  const [attachReload, setAttachReload] = useState(0);
  const load = async () => {
    try { setItems((await api.get("/opportunities")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setStage = async (id, stage) => {
    try { await api.patch(`/opportunities/${id}`, { stage }); toast.success(`Stage → ${stage}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setValue = async (id, value) => {
    try { await api.patch(`/opportunities/${id}`, { expected_value: parseFloat(value || 0) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createProject = async (o) => {
    const name = window.prompt("Project name?", o.name);
    if (!name) return;
    try {
      const r = await api.post("/projects", { opportunity_id: o.id, name, scope_summary: `Created from opportunity ${o.id}` });
      toast.success("Project shell created");
      window.location.href = `/projects/${r.data.id}`;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Simple Kanban rendering — no drag-drop lib installed, use stage select instead
  return (
    <AppShell title="CRM · Opportunities" subtitle={`${items.length} opportunity(ies) · pipeline stages`}>
      {items.length === 0 ? (
        <EmptyState icon={Target} title="No opportunities yet" subtitle="Convert a lead to auto-create an opportunity." />
      ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="opps-kanban">
          {STAGES.map((stage) => {
            const col = items.filter((o) => o.stage === stage);
            const sum = col.reduce((a, o) => a + (o.expected_value || 0), 0);
            return (
              <div key={stage} className="bg-erf-creamSecondary rounded-md p-2 border border-erf-borderCream" data-testid={`opps-col-${stage}`}>
                <div className="mono text-[10px] uppercase tracking-widest text-erf-dark/60 px-1">{stage}</div>
                <div className="mono text-[10px] text-erf-dark/50 mb-2 px-1">{col.length} · ${sum.toLocaleString()}</div>
                <div className="space-y-2 min-h-[80px]">
                  {col.map((o) => (
                    <div key={o.id} className="bg-white border border-erf-borderCream rounded p-2 text-xs cursor-pointer"
                          data-testid={`opps-card-${o.id}`}
                          onClick={() => setAttachId(attachId === o.id ? null : o.id)}>
                      <Link to={`/crm/opportunities/${o.id}`} className="font-nav truncate font-semibold underline block min-h-[44px]" data-testid={`opp-card-open-${o.id}`}>{o.name}</Link>
                      <div className="mono text-[10px] text-erf-dark/60">${Number(o.expected_value||0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {attachId && (
          <div className="mb-4 bg-white border border-erf-borderCream rounded-md p-4" data-testid="opps-attach-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-nav text-erf-dark/60">Scope, budget, and documents for opportunity {attachId.slice(0, 8)}</div>
              <div className="flex items-center gap-2">
                <ComplianceProofButton recordType="opportunity" recordId={attachId} />
                <OpenInMicrosoftMailButton surface="opportunity_detail" recordType="opportunity" recordId={attachId} />
                <InformationIntake
                  recordType="opportunity" recordId={attachId}
                  category="other" confidentiality="confidential"
                  capabilities={["upload", "multi", "paste", "existing", "request_info"]}
                  onChanged={() => setAttachReload((k) => k + 1)}
                />
              </div>
            </div>
            <AttachmentsPanel recordType="opportunity" recordId={attachId} reload={attachReload} />
          </div>
        )}
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Expected value</th>
                <th className="text-left px-3 py-2">Probability</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="border-t border-erf-borderCream/60" data-testid={`opp-row-${o.id}`}>
                  <td className="px-3 py-2 font-nav font-semibold"><Link to={`/crm/opportunities/${o.id}`} className="underline inline-flex min-h-[44px] items-center" data-testid={`opp-open-${o.id}`}>{o.name}</Link></td>
                  <td className="px-3 py-2">
                    <Select value={o.stage} onValueChange={(v) => setStage(o.id, v)}>
                      <SelectTrigger className="h-7 text-[11px] w-40" data-testid={`opp-stage-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" defaultValue={o.expected_value} onBlur={(e) => setValue(o.id, e.target.value)} className="h-7 text-[11px] w-32 font-mono" data-testid={`opp-value-${o.id}`} />
                  </td>
                  <td className="px-3 py-2 mono">{Math.round((o.probability || 0) * 100)}%</td>
                  <td className="px-3 py-2 text-right">
                    {/* FINAL-POLISH P2 — Row-level icon-only Open in Outlook (Owner/Admin). */}
                    <span className="inline-block align-middle mr-1">
                      <OpenInMicrosoftMailButton
                        surface="opportunity_row"
                        recordType="opportunity"
                        recordId={o.id}
                        iconOnly
                      />
                    </span>
                    {["negotiation","proposal","won"].includes(o.stage) && (
                      <Button size="sm" variant="outline" onClick={() => createProject(o)} data-testid={`opp-create-project-${o.id}`}>
                        <Boxes className="w-4 h-4 mr-1" /> Create Project
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </AppShell>
  );
}
